import axios from 'axios';
import { SelectedRecording } from '../../../store/list/types';
import { getSiteId } from '../../../utils/getParamsFromUrlPathname';
import { getUrl } from '@hotjar/webapp/features/Playbacks/store/utils';

/**
 * API Docs
 * https://insights-integration.live.eks.hotjar.com/autodocs/redoc/#tag/Recordings/operation/post__api_v1_sites_%7Bsite_id%7D_recordings_bulk-deletion
 */
type BulkDeletionPayload = {
  /* The IDs of the recordings that need to be deleted. */
  recording_ids: number[];

  /* The 'created' timestamps of the recordings that need to be deleted. */
  /* Delete this once ES is fully deprecated. */
  recording_es_timestamps: number[];

  /* The URL where this action happened, for tracking purposes. */
  source_url?: string;
};

/**
 * Sends an API request that issues a Bulk Deletion for all recordings with the given IDs.
 *
 * @param selectedRecordings The id/created pairs for each recording that needs to be deleted.
 * @param siteId Optionally, the ID of the site that the recording belongs to.
 * @returns (boolean) Whether the Bulk Deletion has successfully started. Regardless of actual deletion,
 *  the API request guarantees that the matched recordings are immediately hidden (soft-deleted).
 */
export async function sendRecordingsBulkDeletion(
  selectedRecordings: SelectedRecording[],
  siteId?: number,
): Promise<boolean> {
  if (selectedRecordings.length === 0) {
    return false;
  }

  const payload: BulkDeletionPayload = {
    recording_ids: selectedRecordings.map((selectedRecording) => selectedRecording.id),
    recording_es_timestamps: selectedRecordings.map(
      (selectedRecording) => selectedRecording.created,
    ),
    source_url: getUrl(),
  };

  const response = await axios.post(
    `/api/v1/sites/${siteId ?? getSiteId()}/recordings/bulk-deletion`,
    payload,
  );
  return response.status === 200;
}
